import type { DeliveryMethodLabels, DeliveryMethodType } from 'src/consts';

import type { DeliveryAddress, DeliveryWindow } from './delivery';
import type { Patient } from './patient';

enum PHARMAKON_CART_SOURCE {
  SOURCE_CAPSULE_OS = 'CapsuleOS',
  SOURCE_AFFILIATE_PORTAL = 'Affiliate Portal',
  SOURCE_IOS = 'iOS',
  SOURCE_PEZ = 'Pez',
  SOURCE_WEB = 'Web',
  SOURCE_PHARMAKON = 'Pharmakon',
}

export enum CART_PRODUCT_TYPE {
  OTCItem = 'OTCItem',
  PrescriptionFulfillment = 'PrescriptionFulfillment',
}

enum REFRIGERATION_TYPE {
  NO_REFRIGERATION = 'no_refrigeration',
  REFRIGERATE = 'refrigerate',
  REFRIGERATE_AFTER_MIXING = 'after_mixing',
}

interface CartProduct {
  id: string;
  type: CART_PRODUCT_TYPE;
  name: string;
  description: string;
  dosage?: string;
  quantity?: string;
  price?: number;
  refrigerationType: REFRIGERATION_TYPE;
}

export interface ShippingOption {
  id: string;
  name: string;
  description: string;
  price: number;
}

export interface CartItem {
  id: string;
  product: CartProduct;
  price: string;
  quantity: number;
}

interface CartPatient extends Patient {
  id: string;
}

interface PrescriptionCartItem {
  patient: CartPatient;
  items: CartItem[];
}

enum ShippingOptionType {
  standard = 'STANDARD',
}

export interface DeliveryMethodOption {
  key: DeliveryMethodType;
  name: string;
  description?: string;
  price?: number;
  showPrice: boolean;
}

export interface DeliveryMethod {
  key: DeliveryMethodType;
  flavorText: string;
  formattedDeliveryTime: string;
  formattedDeliveryDate: string;
  name: string | null;
  deliveryWindow: DeliveryWindow;
  shippingOption: ShippingOption;
  earliestPickupTime: string;
}

export interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expirationDate: string;
  isExpired: boolean;
}

export interface Cart {
  id: string;
  uuid: string;
  accountId: string;
  prescriptionCartItems: PrescriptionCartItem[];
  otcCartItems: CartItem[];
  deliveryAddress?: DeliveryAddress;
  deliveryMethod?: DeliveryMethod;
  paymentMethod?: PaymentMethod;
  subtotal: number;
  shippingCost: number;
  tax: number;
  total: number;
  tip: number;
  preCreditTotal: number;
  source: PHARMAKON_CART_SOURCE;
  otcItemsSubtotal: number;
  verifyId: boolean;
  showPrice: boolean;
}

export type CartItemForCheckout = {
  fulfillmentId?: string;
  otcItemId?: string;
  productId?: string;
  price: number;
  quantity?: number;
  prescriptionId?: string;
  patientMedicationId?: string;
};

export interface CheckoutPickupSectionProps {
  bffCartDeliveryAddress: DeliveryAddress;
  cartId: string;
  dispatchAddressListClicked: () => void;
  dispatchDeliveryWindowsListClicked: () => void;
  deliveryMethodFirstRowText: string;
  deliveryMethodSecondRowText: string;
  hasChosenPickupStationAfterSettingDeliveryMethodAsCourier: boolean;
  selectedDeliveryMethodFromRadioGroup: DeliveryMethodLabels;
  hasAlreadyChosenCourierDeliveryAddress: boolean;
  hasError?: boolean;
}

interface BaseCartRequest {
  cartUUID: string;
}

export interface UpdateCartPaymentMethodRequest extends BaseCartRequest {
  paymentMethodId: string;
}

export interface UpdateCartDeliveryMethodRequest extends BaseCartRequest {
  deliveryWindowId?: string;
  type: DeliveryMethodType;
  shippingOption?: ShippingOptionType;
}

export interface UpdateCartTipRequest {
  tip: number;
}

export interface UpdateCartDeliveryAddressRequest extends BaseCartRequest {
  deliveryAddressId: string;
}
